import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home, Shield, ShieldCheck, FileText, BookOpen, Database, Sparkles, LogOut, LogIn,
  DollarSign, ArrowRight, TrendingUp, TrendingDown, Globe, Zap, Target,
  Clock, CheckCircle2, Layers, BarChart3, Play, Building, Headphones,
  Image as ImageIcon, Presentation, ChevronRight, Loader2, Send,
} from 'lucide-react';
import { toast } from 'sonner';
import LeadCaptureCTA from '../components/LeadCaptureCTA';
import PublicShell from '../components/PublicShell';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NAV = [
  { path: '/', icon: Home, label: 'Home', color: '#0ea5e9' },
  { path: '/platform', icon: Shield, label: 'Intelligence Platform', auth: true, color: '#16a34a' },
  { path: '/documentation', icon: FileText, label: 'Documentation', color: '#2563eb' },
  { path: '/use-cases', icon: BookOpen, label: 'Use Cases', color: '#9333ea' },
  { path: '/sources', icon: Database, label: 'Research Database', color: '#0891b2' },
  { path: '/provenance', icon: ShieldCheck, label: 'Data Provenance', color: '#059669' },
  { path: '/pricing', icon: DollarSign, label: 'Pricing', color: '#ca8a04' },
];

const ROTATING_TARGETS = [
  { country: 'Kenya', sector: 'CEMS & Environmental Services' },
  { country: 'Vietnam', sector: 'Semiconductor Manufacturing' },
  { country: 'Nigeria', sector: 'Fintech Infrastructure' },
  { country: 'Indonesia', sector: 'Clean Energy Transition' },
  { country: 'Brazil', sector: 'Agritech & Food Security' },
  { country: 'UAE', sector: 'Sovereign Wealth Co-Investment' },
];

const BLUEPRINT_EXCERPTS = [
  {
    section: 'Investment Verdict',
    tier: 'SOVEREIGN',
    color: '#7c3aed',
    text: 'STRONG BUY — $52M target equity, 22.3% IRR base case, 7-yr payback. First-mover advantage in regulated CEMS market with 91% under-served industrial capacity.',
  },
  {
    section: 'Market Opportunity',
    tier: 'SCOUT',
    color: '#004ac6',
    text: 'TAM $180M by 2027 (15.4% CAGR). Kenya\'s NEMA regulatory tightening + World Bank $240M green bond creating 3-year demand window. 400 industrial sites currently out of compliance.',
  },
  {
    section: 'Deal Structuring',
    tier: 'SOVEREIGN',
    color: '#7c3aed',
    text: 'PPP with 20-yr concession + IFC blended finance ($18M senior debt @ SOFR+450). KNH as sovereign guarantor. Mitigates FX via revenue indexation to USD at 0.4 cap.',
  },
  {
    section: 'Regulatory Deep Dive',
    tier: 'STRATEGIST',
    color: '#00A651',
    text: 'NEMA Licence Tier 3 required (90-day pathway). KEBS type-approval for hardware (45 days). EPRA tariff review ongoing — Q2\'25 decision creates 120-day entry window.',
  },
];

const PROOF_POINTS = [
  { icon: Clock, value: '90 min', label: 'Blueprint generation', sub: 'vs 6-12 weeks traditional' },
  { icon: DollarSign, value: '$459+', label: 'Per country report', sub: 'vs $50K–$200K consulting' },
  { icon: Layers, value: '31 sections', label: 'Institutional-grade analysis', sub: 'Scout → Strategist → Sovereign' },
  { icon: Database, value: '1,000+', label: 'Data sources', sub: 'World Bank · IMF · UNCTAD' },
];

const WORKFLOW_STEPS = [
  { num: '01', title: 'Pick country + sector', desc: 'From 180+ countries × 115 sectors. Multi-tenant, isolated per user.' },
  { num: '02', title: 'AI drafts 31 sections', desc: 'Intelligence Engine with chunked Part A+B rendering. Each section ~10-12k chars of dense analysis.' },
  { num: '03', title: 'Choose your deliverables', desc: 'PDF, Gamma deck, audio brief, AI illustrations — all generated on-demand.' },
  { num: '04', title: 'Ship to your IC', desc: 'Board-ready PDF. Regenerate sections up to 10× over 3 months as your thesis evolves.' },
];

// ─── A/B Test Variant (hero headline) ───
function useABVariant(experiment) {
  const [variant, setVariant] = useState(null);
  const sessionIdRef = useRef(null);
  useEffect(() => {
    // Persistent variant per browser to keep the experience stable between visits
    let v = localStorage.getItem(`ab_${experiment}`);
    if (v !== 'A' && v !== 'B') {
      v = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem(`ab_${experiment}`, v);
    }
    let sid = localStorage.getItem('ab_session');
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('ab_session', sid);
    }
    sessionIdRef.current = sid;
    setVariant(v);
    // Log impression
    axios.post(`${API}/ab-events`, { experiment, variant: v, event_type: 'impression', session_id: sid }).catch(() => {});
  }, [experiment]);
  const trackClick = (event_type) => {
    if (!variant) return;
    axios.post(`${API}/ab-events`, { experiment, variant, event_type, session_id: sessionIdRef.current }).catch(() => {});
  };
  return { variant, trackClick };
}

// ─── Animated Rotating Hero Target ───
function RotatingTarget() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % ROTATING_TARGETS.length), 2400);
    return () => clearInterval(t);
  }, []);
  const cur = ROTATING_TARGETS[idx];
  return (
    <span key={idx} className="inline-block animate-fadeInUp bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">
      {cur.country} <span className="text-[#434655] text-2xl md:text-3xl lg:text-4xl font-semibold">·</span> {cur.sector}
    </span>
  );
}

// ─── Live Intelligence Ticker ───
function LiveTicker() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/intelligence-feed`)
      .then(r => setItems(r.data.feed?.slice(0, 24) || []))
      .catch(() => {});
  }, []);
  if (!items.length) {
    return (
      <div className="bg-[#0b1c30] py-3 overflow-hidden text-white/70 text-xs font-mono flex items-center justify-center gap-2">
        <Zap className="w-3 h-3 animate-pulse text-[#a78bfa]" />
        <span>Loading live intelligence…</span>
      </div>
    );
  }
  return (
    <div className="bg-[#0b1c30] py-3 overflow-hidden border-y border-white/10 relative">
      <div className="flex gap-8 animate-marquee whitespace-nowrap">
        {[...items, ...items].map((it, i) => {
          const positive = it.raw_value >= 0;
          return (
            <div key={i} className="flex items-center gap-2 text-white/80 text-xs">
              <span className="text-[#a78bfa] font-bold">{it.country}</span>
              <span className="text-white/40">·</span>
              <span>{it.indicator}:</span>
              <span className={`font-mono font-bold ${positive ? 'text-emerald-400' : 'text-red-400'} flex items-center gap-0.5`}>
                {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {it.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Blueprint Excerpt Carousel (auto-rotating) ───
function BlueprintExcerptCarousel() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % BLUEPRINT_EXCERPTS.length), 4500);
    return () => clearInterval(t);
  }, []);
  const cur = BLUEPRINT_EXCERPTS[idx];
  return (
    <div className="bg-white rounded-2xl shadow-2xl p-6 border border-[#c3c6d7]/40 relative overflow-hidden"
         data-testid="blueprint-excerpt-carousel">
      {/* Faux document chrome */}
      <div className="flex items-center gap-1.5 mb-4">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
        <span className="ml-3 text-[10px] font-mono text-[#434655]">kenya-cems-blueprint.pdf · live</span>
        <span className="ml-auto text-[9px] font-mono text-[#004ac6] animate-pulse">● RENDERING</span>
      </div>
      <div key={idx} className="animate-fadeInUp">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded"
                style={{ backgroundColor: cur.color + '1A', color: cur.color }}>
            {cur.tier}
          </span>
          <span className="text-[10px] text-[#737686]">{cur.section}</span>
        </div>
        <p className="text-sm text-[#0b1c30] leading-relaxed font-serif">"{cur.text}"</p>
      </div>
      {/* Progress dots */}
      <div className="flex gap-1.5 mt-5">
        {BLUEPRINT_EXCERPTS.map((_, i) => (
          <div key={i} className={`h-0.5 rounded-full transition-all ${i === idx ? 'w-8 bg-[#004ac6]' : 'w-3 bg-[#c3c6d7]/50'}`} />
        ))}
      </div>
    </div>
  );
}

// ─── Floating AI Asset Badges ───
function FloatingBadges() {
  const badges = [
    { icon: FileText, label: '31 sections', color: '#004ac6', delay: 0 },
    { icon: ImageIcon, label: 'AI visuals', color: '#00A651', delay: 0.3 },
    { icon: Headphones, label: 'Audio brief', color: '#f59e0b', delay: 0.6 },
    { icon: Presentation, label: 'Gamma deck', color: '#7c3aed', delay: 0.9 },
  ];
  return (
    <div className="absolute inset-0 pointer-events-none">
      {badges.map((b, i) => {
        const Icon = b.icon;
        const positions = ['top-0 -left-8', 'top-1/4 -right-10', 'bottom-1/4 -left-12', 'bottom-0 -right-8'];
        return (
          <div key={i}
               className={`absolute ${positions[i]} bg-white rounded-full shadow-lg px-3 py-1.5 flex items-center gap-1.5 animate-float`}
               style={{ animationDelay: `${b.delay}s` }}>
            <Icon className="w-3.5 h-3.5" style={{ color: b.color }} />
            <span className="text-[11px] font-bold text-[#0b1c30]">{b.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Section Generation Demo (fake-live) ───
function GenerationDemo() {
  const [progress, setProgress] = useState(0);
  const [sections, setSections] = useState([]);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let p = 0;
    const SECTION_LIST = [
      { n: '01', name: 'Country Snapshot', tier: 'SCOUT' },
      { n: '02', name: 'Executive Summary', tier: 'SCOUT' },
      { n: '03', name: 'Market Opportunity', tier: 'SCOUT' },
      { n: '13', name: 'Stakeholder Map', tier: 'STRATEGIST' },
      { n: '14', name: 'Regulatory Deep Dive', tier: 'STRATEGIST' },
      { n: '23', name: 'DFI & Blended Finance', tier: 'SOVEREIGN' },
      { n: '28', name: 'IC Investment Memo', tier: 'SOVEREIGN' },
      { n: '30', name: '100-Day Playbook', tier: 'SOVEREIGN' },
    ];
    const t = setInterval(() => {
      p = (p + 1) % (SECTION_LIST.length + 1);
      setProgress(p);
      setSections(SECTION_LIST.slice(0, p));
    }, 900);
    return () => clearInterval(t);
  }, [inView]);

  const total = 8;
  return (
    <div ref={ref} className="bg-[#0b1c30] rounded-2xl p-6 shadow-2xl border border-white/10 relative overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#a78bfa]">● LIVE</p>
          <p className="text-white font-bold tracking-tight text-sm mt-0.5">Generating Kenya blueprint…</p>
        </div>
        <div className="text-white/60 text-xs font-mono">{progress}/{total}</div>
      </div>
      <div className="h-1 bg-white/10 rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-[#004ac6] via-[#00A651] to-[#7c3aed] transition-all duration-700 ease-out"
             style={{ width: `${(progress / total) * 100}%` }} />
      </div>
      <div className="space-y-2 max-h-72 overflow-hidden">
        {sections.map((s, i) => (
          <div key={s.n} className="flex items-center gap-3 text-white/90 text-xs animate-fadeInUp"
               style={{ animationDelay: `${i * 0.05}s` }}>
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span className="text-white/40 font-mono w-6">{s.n}</span>
            <span className="flex-1 truncate">{s.name}</span>
            <span className="text-[8px] font-black tracking-widest px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: (s.tier === 'SCOUT' ? '#004ac6' : s.tier === 'STRATEGIST' ? '#00A651' : '#7c3aed') + '33',
                    color: s.tier === 'SCOUT' ? '#93b5ff' : s.tier === 'STRATEGIST' ? '#6ee7b7' : '#c4b5fd',
                  }}>{s.tier}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Animated Counter ───
function AnimatedCounter({ end, suffix = '', duration = 1500 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated) {
        setAnimated(true);
        const target = parseFloat(String(end).replace(/,/g, ''));
        const start = Date.now();
        const timer = setInterval(() => {
          const elapsed = Date.now() - start;
          const pct = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - pct, 3); // ease-out cubic
          setCount(Math.floor(target * eased));
          if (pct >= 1) clearInterval(timer);
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [animated, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function LandingPage({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();

  useSEO({
    title: 'Sovereign IQ — AI Country Intelligence & Market Entry Blueprints in 90 Minutes',
    description: 'Investment-grade 31-section country blueprints for DFIs, IPAs, MNCs and infrastructure funds. Live data, audit-ready citations, zero hallucinations.',
    keywords: 'country intelligence, emerging markets research, market entry strategy, sovereign risk analysis, PESTLE analysis, DFI development finance, IPA investment promotion, infrastructure PE, AI market research, country blueprint, FDI advisory',
    canonical: 'https://sovereigniq.org/',
  });

  const { variant, trackClick } = useABVariant('landing_hero_headline');
  const { variant: ctaVariant, trackClick: trackCtaClick } = useABVariant('landing_cta_copy');
  const go = (path, auth) => navigate(auth && !user ? '/login' : path);

  // ─── Dynamic pricing — same source of truth as PublicPricingPage ───
  // Fetched from /api/pricing-config, which the admin populates via the
  // "Publish to Customers" button on the internal Pricing page.
  // Defaults mirror PublicPricingPage's DEFAULT_BASE_COSTS so the price block
  // renders something sensible while the network request is in flight.
  const DEFAULT_BASE_COSTS = { Scout: 1.04, Strategist: 3.58, Sovereign: 4.69 };
  const [pricingMarkup, setPricingMarkup] = useState(1000);
  const [pricingBaseCosts, setPricingBaseCosts] = useState(DEFAULT_BASE_COSTS);
  const [pricingPromo, setPricingPromo] = useState(null);
  const [pricingLoaded, setPricingLoaded] = useState(false);
  // Be Lucky bar — gated by admin's `show_lucky_bar` flag on /pricing-config.
  const [showLuckyBar, setShowLuckyBar] = useState(false);
  const [luckyCountries, setLuckyCountries] = useState([]);
  const [luckySectors, setLuckySectors] = useState([]);
  const [luckyCountry, setLuckyCountry] = useState('');
  const [luckySector, setLuckySector] = useState('');
  const [luckyMessage, setLuckyMessage] = useState('');
  const [luckySubmitting, setLuckySubmitting] = useState(false);
  const [luckySubmitted, setLuckySubmitted] = useState(false);
  // Live "audit trail" badge — counts come from /api/public/provenance.
  const [provenanceStats, setProvenanceStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pricing-config`).then(res => {
      setPricingMarkup(res.data.markup || 1000);
      const liveBase = res.data.base_costs || {};
      setPricingBaseCosts({
        Scout:      liveBase.Scout      != null ? liveBase.Scout      : DEFAULT_BASE_COSTS.Scout,
        Strategist: liveBase.Strategist != null ? liveBase.Strategist : DEFAULT_BASE_COSTS.Strategist,
        Sovereign:  liveBase.Sovereign  != null ? liveBase.Sovereign  : DEFAULT_BASE_COSTS.Sovereign,
      });
      const active = (res.data.promotions || []).find(p => p.active);
      setPricingPromo(active || null);
      setShowLuckyBar(res.data.show_lucky_bar !== false);
      setPricingLoaded(true);
    }).catch(() => setPricingLoaded(true));
    // Live provenance counts — feed the "X countries audited · Y observations
    // on file · live" badge above the hero CTA. Cheap query (cached aggregate
    // in the API), so we just refresh on every page load.
    axios.get(`${API}/public/provenance`).then(res => {
      const list = res.data?.countries || [];
      const totalObs = list.reduce((a, c) => a + (c.observations || 0), 0);
      const providers = new Set(list.flatMap(c => c.providers || []));
      setProvenanceStats({
        countries:    list.length,
        observations: totalObs,
        providers:    providers.size,
      });
    }).catch(() => {});
    // Sectors + countries are needed by the Be Lucky form's dropdowns; fetch
    // them in parallel so the form is ready the moment it becomes visible.
    axios.get(`${API}/sectors`).then(res => setLuckySectors(res.data.sectors || [])).catch(() => {});
    axios.get(`${API}/countries-list`).then(res => setLuckyCountries(res.data.countries || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // For a given Title-Case tier name, return { fullPrice, discountedPrice|null }.
  const computeTierPrice = (tierTitleCase) => {
    const base = pricingBaseCosts[tierTitleCase] != null ? pricingBaseCosts[tierTitleCase] : DEFAULT_BASE_COSTS[tierTitleCase];
    const fullPrice = Math.round(base * pricingMarkup);
    const discountedPrice = pricingPromo ? Math.round(fullPrice * (1 - pricingPromo.discount / 100)) : null;
    return { fullPrice, discountedPrice };
  };

  const handleLuckySubmit = async () => {
    if (!user) { navigate('/login?redirect=/'); return; }
    if (!luckyCountry.trim() || !luckySector.trim()) { toast.error('Please select a country and sector'); return; }
    setLuckySubmitting(true);
    try {
      await axios.post(`${API}/free-access-requests`, { country: luckyCountry, sector: luckySector, message: luckyMessage });
      toast.success('Request submitted! The admin will review it shortly.');
      setLuckySubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLuckySubmitting(false);
    }
  };

  return (
    <PublicShell user={user} onLogout={onLogout}>
      <div className="overflow-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Inject landing-only CSS animations */}
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeInUp { animation: fadeInUp 0.5s ease-out forwards; }
        @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-8px); } }
        .animate-float { animation: float 4s ease-in-out infinite; }
        @keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 60s linear infinite; }
        @keyframes slowSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-slowSpin { animation: slowSpin 30s linear infinite; }
        @keyframes glow { 0%, 100% { box-shadow: 0 0 40px rgba(0, 74, 198, 0.2); } 50% { box-shadow: 0 0 80px rgba(124, 58, 237, 0.35); } }
        .animate-glow { animation: glow 4s ease-in-out infinite; }
        .grid-bg { background-image: linear-gradient(rgba(0, 74, 198, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 74, 198, 0.05) 1px, transparent 1px); background-size: 40px 40px; }
      `}</style>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-auto" data-testid="landing-page">
        {/* ───────── HERO ───────── */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#f8f9ff] via-white to-[#eff4ff] border-b border-[#c3c6d7]/20">
          <div className="absolute inset-0 grid-bg opacity-60" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-[#004ac6]/5 via-[#7c3aed]/5 to-transparent animate-slowSpin pointer-events-none" />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 bg-white border border-[#c3c6d7]/40 rounded-full px-3 py-1 mb-6 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#434655]">Live intelligence · real-time</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0b1c30] leading-[1.05] mb-6" data-testid={`hero-h1-variant-${variant || 'loading'}`}>
                {variant === 'B' ? (
                  <>
                    Every country. Every sector.
                    <br />
                    <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">
                      Every insight your IC needs
                    </span>
                    <br />
                    — in 90 minutes.
                  </>
                ) : (
                  <>
                    Your next $50M move into
                    <br />
                    <RotatingTarget />
                    <br />
                    deserves better than Google.
                  </>
                )}
              </h1>

              <p className="text-base lg:text-lg text-[#434655] leading-relaxed mb-8 max-w-2xl">
                Sovereign IQ generates <strong>31-section, investment-grade country blueprints</strong> in under 90 minutes.
                Powered by our AI intelligence engine, real-time World Bank + IMF data, and 1,000+ authoritative sources — so investment committees can ship conviction, not caveats.
              </p>

              <div className="flex items-center gap-3 flex-wrap mb-10">
                <button onClick={() => { trackClick('cta_primary_click'); trackCtaClick('cta_primary_click'); go('/platform', true); }} data-testid={`hero-cta-primary-${ctaVariant || 'loading'}`}
                  className="group flex items-center gap-2 bg-[#0b1c30] text-white px-6 py-3.5 rounded-lg text-sm font-bold hover:bg-[#1a2d4a] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5">
                  <Sparkles className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                  {user
                    ? 'Enter Intelligence Platform'
                    : (ctaVariant === 'B' ? 'Start free trial' : 'Generate your first blueprint')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <button onClick={() => { trackClick('cta_secondary_click'); navigate('/pricing'); }} data-testid="hero-cta-secondary"
                  className="flex items-center gap-2 bg-white text-[#004ac6] px-6 py-3.5 rounded-lg text-sm font-bold border border-[#004ac6]/20 hover:bg-[#eff4ff] transition-all">
                  <DollarSign className="w-4 h-4" /> See pricing
                </button>
                <button onClick={() => navigate('/use-cases')}
                  className="flex items-center gap-2 text-[#434655] px-3 py-3.5 text-sm font-semibold hover:text-[#004ac6] transition-all">
                  <Play className="w-4 h-4" /> See 6 live use cases
                </button>
              </div>

              {/* Live Provenance badge — concrete proof of zero-hallucination */}
              {provenanceStats && provenanceStats.observations > 0 && (
                <div className="mb-8 -mt-4">
                  <button onClick={() => navigate('/provenance')} data-testid="hero-provenance-badge"
                    className="group inline-flex items-center gap-3 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200 hover:border-emerald-400 hover:shadow-md transition-all text-left">
                    <span className="relative flex h-2 w-2 shrink-0">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                    <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0 text-xs">
                      <span className="font-extrabold text-emerald-900">{provenanceStats.countries.toLocaleString()}</span>
                      <span className="text-emerald-800">{provenanceStats.countries === 1 ? 'country audited' : 'countries audited'}</span>
                      <span className="text-emerald-400">·</span>
                      <span className="font-extrabold text-emerald-900">{provenanceStats.observations.toLocaleString()}</span>
                      <span className="text-emerald-800">{provenanceStats.observations === 1 ? 'observation' : 'observations'} on file</span>
                      <span className="text-emerald-400">·</span>
                      <span className="text-emerald-700">{provenanceStats.providers} sources</span>
                      <span className="text-emerald-400">·</span>
                      <span className="text-emerald-600 italic">live</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 group-hover:text-emerald-900 ml-1 inline-flex items-center gap-1">
                      Browse audit trail <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </button>
                </div>
              )}

              {/* Trust strip */}
              <div className="flex items-center gap-6 flex-wrap text-[11px] text-[#737686]">
                <span className="font-semibold uppercase tracking-widest">Built for</span>
                <span className="flex items-center gap-1.5"><Building className="w-3.5 h-3.5" /> PE & VC funds</span>
                <span className="flex items-center gap-1.5"><Globe className="w-3.5 h-3.5" /> DFIs & IFIs</span>
                <span className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5" /> Multinationals</span>
                <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Sovereign funds</span>
              </div>
            </div>

            {/* Right visual — blueprint excerpt + generation demo */}
            <div className="lg:col-span-5 space-y-4 relative">
              <div className="animate-glow rounded-2xl">
                <BlueprintExcerptCarousel />
              </div>
              <FloatingBadges />
              <GenerationDemo />
            </div>
          </div>
        </section>

        {/* ───────── LIVE TICKER ───────── */}
        <LiveTicker />

        {/* ───────── PROOF POINTS ───────── */}
        <section className="relative py-16 lg:py-20 border-b border-[#c3c6d7]/20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#004ac6] mb-3">Traditional consulting vs Sovereign IQ</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-[#0b1c30] mb-4">
                Same boardroom rigor.
                <br />
                <span className="bg-gradient-to-r from-[#004ac6] to-[#7c3aed] bg-clip-text text-transparent">
                  1,000× faster. 100× cheaper.
                </span>
              </h2>
              <p className="text-sm text-[#434655]">Investment committees shouldn't wait 8 weeks for market intelligence. Sovereign IQ compresses the entire country-entry workflow into a 12-minute render — without sacrificing depth.</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {PROOF_POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="bg-gradient-to-br from-white to-[#f8f9ff] rounded-2xl p-6 border border-[#c3c6d7]/30 hover:border-[#004ac6]/40 hover:-translate-y-1 transition-all">
                    <div className="w-10 h-10 rounded-xl bg-[#0b1c30] flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-3xl font-extrabold tracking-tighter text-[#0b1c30] mb-1">{p.value}</div>
                    <div className="text-xs font-semibold text-[#004ac6] mb-1">{p.label}</div>
                    <div className="text-[11px] text-[#737686]">{p.sub}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ───────── WORKFLOW ───────── */}
        <section className="py-16 lg:py-20 bg-[#f8f9ff] border-b border-[#c3c6d7]/20">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#004ac6] mb-3">How it works</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-[#0b1c30] mb-4">
                From blank page to IC-ready deck in 90 minutes.
              </h2>
            </div>

            <div className="grid lg:grid-cols-4 gap-6 relative">
              {/* Connector line */}
              <div className="hidden lg:block absolute top-14 left-8 right-8 h-0.5 bg-gradient-to-r from-[#004ac6]/20 via-[#00A651]/20 to-[#7c3aed]/20" />
              {WORKFLOW_STEPS.map((step, i) => (
                <div key={i} className="relative bg-white rounded-2xl p-6 border border-[#c3c6d7]/30 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0b1c30] to-[#1a365d] text-white flex items-center justify-center font-extrabold text-sm tracking-tighter mb-4 shadow-lg relative z-10">
                    {step.num}
                  </div>
                  <h3 className="text-base font-bold tracking-tight text-[#0b1c30] mb-2">{step.title}</h3>
                  <p className="text-xs text-[#434655] leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ───────── TIER SHOWCASE ───────── */}
        <section className="py-16 lg:py-20 border-b border-[#c3c6d7]/20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#004ac6] mb-3">Three tiers · One platform</p>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tighter text-[#0b1c30] mb-4">
                Scout. Strategist. Sovereign.
              </h2>
              <p className="text-sm text-[#434655]">Buy the depth you need — upgrade when your deal demands more. Each tier inherits everything beneath it.</p>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
              {[
                { name: 'SCOUT',      title: 'Scout',      color: '#004ac6', bg: '#eff4ff', tagline: 'Reconnaissance',         features: ['12 Scout-tier AI sections', 'Investment-grade PDF export', 'Real-time World Bank data', '1,000+ source references'] },
                { name: 'STRATEGIST', title: 'Strategist', color: '#00A651', bg: '#ecfdf5', tagline: 'Strategic execution', popular: true, features: ['Everything in Scout', '+10 Strategist sections (22 total)', 'AI-generated illustrations', 'Gamma presentation deck', 'Investment infographic'] },
                { name: 'SOVEREIGN',  title: 'Sovereign',  color: '#7c3aed', bg: '#f3f0ff', tagline: 'Deal-ready intelligence', features: ['Everything in Strategist', '+9 Sovereign sections (31 total)', 'IC-ready investment memo', '100-day playbook', 'Capital markets & listing pathways', 'Professional audio narration'] },
              ].map((tier, i) => {
                const { fullPrice, discountedPrice } = computeTierPrice(tier.title);
                return (
                <div key={tier.name} className={`relative rounded-2xl p-6 border-2 transition-all hover:-translate-y-1 ${tier.popular ? 'shadow-2xl' : 'shadow-sm hover:shadow-lg'}`}
                     style={{ borderColor: tier.popular ? tier.color : '#e5e7eb', backgroundColor: 'white' }}>
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00A651] text-white text-[10px] font-black tracking-widest px-3 py-1 rounded-full">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-black uppercase tracking-widest" style={{ color: tier.color }}>{tier.name}</span>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: tier.bg }}>
                      <Sparkles className="w-4 h-4" style={{ color: tier.color }} />
                    </div>
                  </div>
                  <p className="text-xs text-[#737686] mb-1">{tier.tagline}</p>
                  {discountedPrice != null ? (
                    <div className="mb-4" data-testid={`home-tier-price-${tier.name.toLowerCase()}`}>
                      <span className="text-sm text-[#c3c6d7] line-through font-semibold mr-2">${fullPrice.toLocaleString()}</span>
                      <p className="text-3xl font-extrabold tracking-tighter text-[#0b1c30]">From ${discountedPrice.toLocaleString()}<span className="text-xs text-[#737686] font-semibold"> / report</span></p>
                      <span className="inline-block mt-1 text-[10px] font-bold text-white bg-red-500 px-2 py-0.5 rounded-full">SAVE ${(fullPrice - discountedPrice).toLocaleString()}</span>
                    </div>
                  ) : (
                    <p className="text-3xl font-extrabold tracking-tighter text-[#0b1c30] mb-4" data-testid={`home-tier-price-${tier.name.toLowerCase()}`}>
                      From ${pricingLoaded ? fullPrice.toLocaleString() : '…'}<span className="text-xs text-[#737686] font-semibold"> / report</span>
                    </p>
                  )}
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs text-[#434655]">
                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: tier.color }} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => navigate('/pricing')} data-testid={`home-tier-cta-${tier.name.toLowerCase()}`}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold transition-all"
                    style={{ backgroundColor: tier.color, color: 'white' }}>
                    View {tier.name.charAt(0) + tier.name.slice(1).toLowerCase()} details <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                );
              })}
            </div>

            {/* ── Be Lucky Banner ── (admin-toggleable on /platform/pricing) */}
            {showLuckyBar && (
              <div className="max-w-5xl mx-auto mt-10" data-testid="home-be-lucky-bar">
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="px-6 py-3 flex items-center gap-2 bg-amber-400/20">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-bold text-amber-800">Be Lucky</span>
                    <span className="text-xs text-amber-600 ml-1">— Ask the administrator for complimentary reports</span>
                  </div>
                  {luckySubmitted ? (
                    <div className="px-6 py-4 text-center">
                      <p className="text-sm font-semibold text-green-700">Request submitted successfully!</p>
                      <p className="text-xs text-[#434655] mt-1">A confirmation email has been sent to you.</p>
                    </div>
                  ) : (
                    <div className="px-6 py-4">
                      <div className="flex gap-3 items-end flex-wrap">
                        <div className="flex-1 min-w-[160px]">
                          <label className="text-[10px] text-[#434655] block mb-1">Country</label>
                          <select value={luckyCountry} onChange={e => setLuckyCountry(e.target.value)}
                            className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none appearance-none"
                            data-testid="home-lucky-country">
                            <option value="">Select a country...</option>
                            {luckyCountries.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <label className="text-[10px] text-[#434655] block mb-1">Sector</label>
                          <select value={luckySector} onChange={e => setLuckySector(e.target.value)}
                            className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none appearance-none"
                            data-testid="home-lucky-sector">
                            <option value="">Select a sector...</option>
                            {luckySectors.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[140px]">
                          <label className="text-[10px] text-[#434655] block mb-1">Message (optional)</label>
                          <input type="text" value={luckyMessage} onChange={e => setLuckyMessage(e.target.value)}
                            placeholder="Why you'd like a free report..."
                            className="w-full bg-white border border-[#c3c6d7]/40 rounded-lg px-3 py-2 text-sm text-[#0b1c30] focus:ring-1 focus:ring-amber-400 outline-none"
                            data-testid="home-lucky-message" />
                        </div>
                        <button onClick={handleLuckySubmit} disabled={luckySubmitting}
                          data-testid="home-lucky-submit"
                          className="bg-amber-500 text-white px-5 py-2 rounded-lg text-sm font-bold hover:bg-amber-600 transition-all flex items-center gap-1.5 shrink-0 disabled:opacity-50">
                          {luckySubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          {user ? 'Submit Request' : 'Sign In to Submit'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ───────── STATS + TRUST ───────── */}
        <section className="py-14 bg-gradient-to-br from-[#0b1c30] via-[#1a365d] to-[#004ac6] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { v: 1000, s: '+', label: 'Curated sources' },
              { v: 145, s: '', label: 'API integrations' },
              { v: 180, s: '+', label: 'Countries covered' },
              { v: 30, s: '', label: 'Investment-grade sections' },
            ].map((st, i) => (
              <div key={i}>
                <div className="text-4xl md:text-5xl font-extrabold tracking-tighter bg-gradient-to-r from-white to-[#a78bfa] bg-clip-text text-transparent">
                  <AnimatedCounter end={st.v} suffix={st.s} />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-widest text-white/60 mt-1">{st.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ───────── LEAD CAPTURE CTA ───────── */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <LeadCaptureCTA source_page="landing"
            headline="Ready to ship your next country thesis?"
            subline="Generate your first blueprint in under 90 minutes, or schedule a 20-min walkthrough with our team. We work with investment funds, DFIs, governments, and multinationals." />
        </div>
      </main>
      </div>
    </PublicShell>
  );
}
